// Atalhos para os tipos do contrato.
//
// contrato.ts é GERADO a partir de api/openapi.yaml e não se edita à mão. Este
// arquivo só dá nomes curtos ao que vem de lá, para as telas escreverem
// `Mensagem` em vez de `components["schemas"]["MensagemResumo"]`.
//
// O ganho não é digitar menos: é que mudar o contrato passa a QUEBRAR A
// COMPILAÇÃO nas telas que dependiam do campo removido, em vez de virar
// `undefined` em produção.

import type { components } from "./contrato";

type E = components["schemas"];

export type Problema = E["Problema"];
export type Pagina = E["Pagina"];
export type PaginaCursor = E["PaginaCursor"];

export type Sessao = E["Sessao"];
export type SessaoAberta = E["SessaoAberta"];
export type SenhaApp = E["SenhaApp"];
export type Nivel = E["Nivel"];

export type Pasta = E["Pasta"];
export type Cota = E["Cota"];
export type MensagemResumo = E["MensagemResumo"];
export type Mensagem = E["Mensagem"];
export type Anexo = E["Anexo"];
export type MensagemEnvio = E["MensagemEnvio"];
export type MensagemQuarentena = E["MensagemQuarentena"];

export type Revenda = E["Revenda"];
export type Contato = E["Contato"];
export type ServidorOrigem = E["ServidorOrigem"];
// A ENTRADA também vem do contrato. Estava escrita à mão em admin.ts como
// `tipo?: "whm" | "imap"`, e quando o `directadmin` entrou no contrato nada
// quebrou: o backend ficou pronto e a tela não conseguia mandar o valor
// novo. É o mesmo defeito que o bloco no fim deste arquivo descreve.
export type ServidorOrigemEntrada = E["ServidorOrigemEntrada"];
export type TrabalhoMigracao = E["TrabalhoMigracao"];
export type MaildirOrfao = E["MaildirOrfao"];
export type RevendaEntrada = E["RevendaEntrada"];
export type Dominio = E["Dominio"];
export type DominioEntrada = E["DominioEntrada"];
export type Caixa = E["Caixa"];
export type CaixaEntrada = E["CaixaEntrada"];
export type Alias = E["Alias"];
export type AliasEntrada = E["AliasEntrada"];

export type EventoEntrega = E["EventoEntrega"];
export type ItemFila = E["ItemFila"];
export type RegistroAuditoria = E["RegistroAuditoria"];
export type ConferenciaDns = E["ConferenciaDns"];
export type DiagnosticoDns = E["DiagnosticoDns"];
export type AntispamDominio = E["AntispamDominio"];

/** Página do contrato, com os itens tipados. */
export type PaginaDe<T> = Pagina & { itens: T[] };
/** Página por cursor, usada na lista de mensagens. */
export type CursorDe<T> = PaginaCursor & { itens: T[] };

/**
 * A hierarquia, em ordem. Existe em código porque a interface precisa
 * comparar níveis ("é pelo menos revenda?"), e comparar string solta é como
 * um `nivel === "revenda"` esquecido vira uma tela mostrada a quem não devia.
 *
 * O servidor NÃO confia nisto: é só para decidir o que desenhar. Quem autoriza
 * é a API, em toda requisição.
 */
export const NIVEIS: readonly Nivel[] = ["usuario", "admin_dominio", "revenda", "super_admin"];

export function peloMenos(atual: Nivel | undefined, minimo: Nivel): boolean {
  if (!atual) return false;
  return NIVEIS.indexOf(atual) >= NIVEIS.indexOf(minimo);
}

// ---------------------------------------------------------------------------
// O TIPO DA RESPOSTA VEM DO CONTRATO, não da minha memória.
//
// Isto existe por causa de um defeito real: as funções de conta.ts declaravam
// `{ itens: SenhaApp[] }` e a API devolve um ARRAY PURO. O contrato sempre
// disse array — quem inventou o envelope fui eu, escrevendo o tipo à mão.
//
// Os tipos dos ESQUEMAS já vinham do contrato, e por isso nunca divergiram.
// Os das RESPOSTAS não vinham, e divergiram na primeira oportunidade. Com
// `Resposta<"/auth/sessoes">` o contrato passa a ser a fonte dos dois, e uma
// mudança nele quebra a compilação aqui em vez de virar `undefined` na tela.
// ---------------------------------------------------------------------------

import type { paths } from "./contrato";

/** O corpo de sucesso de uma operação, qualquer que seja o código dela. */
type CorpoDeSucesso<Op> = Op extends { responses: infer R }
  ? R extends { 200: { content: { "application/json": infer C } } }
    ? C
    : R extends { 201: { content: { "application/json": infer C } } }
      ? C
      : R extends { 202: { content: { "application/json": infer C } } }
        ? C
        : void
  : never;

/**
 * `Resposta<"/auth/sessoes">` é o que o GET daquela rota devolve.
 * `Resposta<"/auth/senhas-app", "post">` para os demais métodos.
 */
export type Resposta<
  Caminho extends keyof paths,
  Metodo extends keyof paths[Caminho] = "get",
> = CorpoDeSucesso<paths[Caminho][Metodo]>;
